// Per-nav perf instrumentation.
// Each `navigateToSession` click stamps `__shellNavT0` on window; downstream
// steps log relative timings. Gated to dev builds — `markPerfT0` is the only
// writer of the stamp and returns early outside DEV, so every reader below is
// silent in production without needing its own gate.
//
// In ts_sdk rather than under `ui/src/routes/loaders/` because the SDK needs it
// too: `FlowSync/context.ts`, `process/agentic-process.ts` and
// `services/shell/ptyConnection.ts` had each hand-rolled this, casting `window`
// themselves because they could not see the declaration.
//
// The same steps also emit under the `process_load` toplog tag (runtime
// toggleable, no T0 stamp needed) so a slow tab-switch can be traced in any
// session — `toplog.on('process_load')` — without pre-instrumenting the click.
// They are co-tagged `agentic_process.load`, the topic for the whole session
// start (click → createProcess → navigate → /open → attach), front and back.

import { toplog } from '../services/toplog';

declare global {
  interface Window {
    /** Per-nav perf T0, stamped by `markPerfT0()` below and read by `perfLog()`
     *  / `perfTime()`. Declared HERE, in the module that owns and stamps the
     *  key, rather than in one of its readers. */
    __shellNavT0?: number;
  }
}

/** The window key this module stamps — the source of truth for the two
 *  accessors below, so the literal is written once. */
export const PERF_T0_KEY = '__shellNavT0';

function readT0(): number | undefined {
  return window[PERF_T0_KEY];
}

export function perfLog(label: string): void {
  toplog.log(['process_load', 'agentic_process.load'], label);
  if (!import.meta.env.DEV) return;
  const t0 = readT0();
  if (t0 === undefined) return;
  console.log(`[PERF] +${(performance.now() - t0).toFixed(0)}ms ${label}`);
}

export async function perfTime<T>(label: string, fn: () => T | Promise<T>): Promise<T> {
  const tagOn = toplog.isOn(['process_load', 'agentic_process.load']);
  if (!import.meta.env.DEV && !tagOn) return fn(); // zero-cost when nothing listens
  const t0 = readT0();
  const start = performance.now();
  try {
    return await fn();
  } finally {
    const dur = performance.now() - start;
    if (tagOn) toplog.log(['process_load', 'agentic_process.load'], `${label} took ${dur.toFixed(1)}ms`);
    if (import.meta.env.DEV && t0 !== undefined) {
      console.log(`[PERF] +${(performance.now() - t0).toFixed(0)}ms ${label} took ${dur.toFixed(1)}ms`);
    }
  }
}

export function markPerfT0(): void {
  if (!import.meta.env.DEV) return;
  window[PERF_T0_KEY] = performance.now();
}
